import { zeroErrorMessage } from "@/lib/zero-error";
import { useState } from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import { toast } from "sonner";

export type PublicHoliday = Tables<"public_holidays">;
export type PublicHolidayInsert = TablesInsert<"public_holidays">;
export type PublicHolidayUpdate = TablesUpdate<"public_holidays">;

type ZeroPublicHoliday = {
  id: string;
  name: string;
  date: number | string;
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

function toLegacyDate(value: number | string | null | undefined) {
  if (typeof value === "number")
    return new Date(value).toISOString().slice(0, 10);
  return value ?? "";
}

function toLegacyTimestamp(value: number | string | null | undefined) {
  if (typeof value === "number") return new Date(value).toISOString();
  return value ?? "";
}

function toLegacyHoliday(row: ZeroPublicHoliday): PublicHoliday {
  return {
    id: row.id,
    name: row.name,
    date: toLegacyDate(row.date),
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

async function reportServerMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

export function usePublicHolidays(year?: number) {
  const [data, details] = useQuery(appQueries.publicHolidays.list({ year }));
  const holidays = ((data ?? []) as ZeroPublicHoliday[]).map(toLegacyHoliday);

  return {
    data: holidays,
    isLoading: details.type !== "complete" && holidays.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreatePublicHoliday() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (holiday: PublicHolidayInsert) => {
    const input = {
      id: holiday.id ?? generateUUID(),
      name: holiday.name,
      date: holiday.date,
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.publicHolidays.create(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Public holiday create was rejected",
      );
      return toLegacyHoliday(input);
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      holiday: PublicHolidayInsert,
      options?: MutationOptions<PublicHoliday>,
    ) => {
      void run(holiday)
        .then((data) => {
          toast.success("Public holiday added");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to add public holiday: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdatePublicHoliday() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async ({
    id,
    updates,
  }: {
    id: string;
    updates: PublicHolidayUpdate;
  }) => {
    const input = {
      id,
      ...(updates.name !== undefined ? { name: updates.name } : {}),
      ...(updates.date !== undefined ? { date: updates.date } : {}),
    };
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.publicHolidays.update(input));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Public holiday update was rejected",
      );
      return toLegacyHoliday({
        id,
        name: input.name ?? "",
        date: input.date ?? "",
      });
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (
      input: { id: string; updates: PublicHolidayUpdate },
      options?: MutationOptions<PublicHoliday>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Public holiday updated");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to update public holiday: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeletePublicHoliday() {
  const zero = useZero() as ZeroMutateClient;
  const [isPending, setIsPending] = useState(false);

  const run = async (id: string) => {
    setIsPending(true);
    try {
      const mutation = zero.mutate(appMutators.publicHolidays.delete({ id }));
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Public holiday delete was rejected",
      );
    } finally {
      setIsPending(false);
    }
  };

  return {
    mutate: (id: string, options?: { onSuccess?: () => void }) => {
      void run(id)
        .then(() => {
          toast.success("Public holiday deleted");
          options?.onSuccess?.();
        })
        .catch((error) =>
          toast.error("Failed to delete public holiday: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
